import React, { useState, useEffect, Suspense, lazy, FC } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useMsal, useIsAuthenticated } from '@azure/msal-react'
import * as jose from 'jose'
import tokenUtils from '../authConfig/utils'
import { signOutClickHandler } from '../authConfig/login'

import { OnboardingContextProvider } from './components/Core/Context/OnboardingContext'
import Main from 'src/components/Core/Styles/Main'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'

import { UserStatusType, TokenDataType } from '../authConfig/types'
import NoApplications from './views/Pages/NoApplications/NoApplications'
import GeneralMessage from './views/Pages/Onboarding/GeneralMessage/GeneralMessage'
import DocDashboard from './views/Pages/DocDashboard/DocDashboard'

const Header = lazy(() => import('src/components/Sections/Header/Header'))
const Footer = lazy(() => import('src/components/Sections/Footer/Footer'))
const Modal = lazy(() => import('src/components/Sections/Modal/Modal'))
const Home = lazy(() => import('src/views/Pages/Home/Home'))
const NoAccountAssociated = lazy(() => import('src/views/Pages/NoAccountAssociated/NoAccountAssociated'))
const UserRejected = lazy(() => import('src/views/Pages/UserRejected/UserRejected'))
const Onboarding = lazy(() => import('src/views/Pages/Onboarding/Onboarding'))
const Dashboard = lazy(() => import('src/views/Pages/Dashboard/Dashboard'))
const ClearanceStatements = lazy(() => import('src/views/Pages/ClearanceStatements/ClearanceStatements'))
const Reports = lazy(() => import('src/views/Pages/Reports/Reports'))
const News = lazy(() => import('src/views/Pages/News/News'))
const Profile = lazy(() => import('src/views/Pages/Profile/Profile'))
const ManageClient = lazy(() => import('src/views/Pages/ManageClient/ManageClient'))
const ManageTeam = lazy(() => import('src/views/Pages/ManageTeam/ManageTeam'))
const Error = lazy(() => import('src/views/Pages/Error/Error'))
const Messages = lazy(() => import('src/views/Pages/Messages/Messages'))
const ManageClearance = lazy(() => import('src/views/Pages/ManageClient/ManageClearance/ManageClearance'))
const DashboardWrapper = lazy(() => import('src/views/Pages/DashboardWrapper/DashboardWrapper'))
const Settlements = lazy(() => import('src/views/Pages/Settlements/Settlements'))
const FooterSettlements = lazy(() => import('src/components/Sections/Footer/FooterSettlements'))

const ControlPanel = lazy(() => import('src/views/Pages/Settlements/Components/ControlPanel/ControlPanel'))
const PositionsOutstanding = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/PositionsOutstanding'))
const PositionsAgreed = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Agreed/PositionsAgreed'))
const CashLedger = lazy(() => import('src/views/Pages/Settlements/Components/CashLedger'))
const PositionsTab = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/Tabs/PositionsTab'))
const TradesTab = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/Tabs/TradesTab'))
const SummaryTab = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/Tabs/SummaryTab'))

const EmployeeControlPanel = lazy(() => import('src/views/Pages/Settlements/Components/ControlPanel/EmployeeControlPanel'))
const EmployeePositionsOutstanding = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/EmployeePositionsOutstanding'))
const EmployeePositionsTab = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/Tabs/EmployeePositionsTab'))
const EmployeeTradesTab = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/Tabs/EmployeeTradesTab'))
const EmployeeSummaryTab = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Outstanding/Tabs/EmployeeSummaryTab'))
const EmployeePositionsAgreed = lazy(() => import('src/views/Pages/Settlements/Components/Positions/Agreed/EmployeePositionsAgreed'))
const EmployeeCashLedger = lazy(() => import('src/views/Pages/Settlements/Components/EmployeeCashLedger/EmployeeCashLedger'))
const PendingPayments = lazy(() => import('src/views/Pages/Settlements/Components/PendingPayments/PendingPayments'))

const App: FC = () => {
  const isAuthenticated = useIsAuthenticated()
  const { instance, accounts } = useMsal()
  const navigate = useNavigate()
  const location = useLocation()
  const [token, setToken] = useState(tokenUtils?.getTokenInitialState('main'))
  const [tokenOnboarding, setTokenOnboarding] = useState(tokenUtils?.getTokenInitialState('onboarding'))
  const [userStatus, setUserStatus] = useState(null)
  const [logoutFlag, setLogoutFlag] = useState(false)
  const [isReloading, setReloading] = useState(false)

  const handleGetTokenError = () => {
    if (!window.location.pathname.includes('/error')) {
      logout()
    }
  }

  useEffect(() => {
    if (isAuthenticated && !token) tokenUtils?.getToken(
      'main',
      instance,
      accounts?.[0],
      (accessToken: string): void => setToken(accessToken),
      handleGetTokenError
    )

    if (isAuthenticated && token && !location?.pathname?.includes('/error/')) {
      const tokenData: TokenDataType = jose?.decodeJwt(token)

      tokenUtils?.checkUserStatus(tokenData?.extension_UserStatus)

      const data: UserStatusType = {
        status: tokenData?.extension_UserStatus
      }

      setUserStatus({
        ...data,
        ...tokenUtils?.parseUserStatus(data)
      })

      if (data?.status === 'Prospect') tokenUtils?.getToken(
        'onboarding',
        instance,
        accounts?.[0],
        (accessToken: string): void => setTokenOnboarding(accessToken),
        handleGetTokenError
      )
    }
  }, [isAuthenticated, token])

  useEffect(() => {
    if (
      userStatus &&
      location?.pathname === '/' &&
      (userStatus?.status !== 'Prospect' || tokenOnboarding)
    ) {
      navigate(userStatus?.navigateTo)
    }
  }, [userStatus, tokenOnboarding])

  useEffect(() => {
    if (logoutFlag && location?.pathname === '/') {
      signOutClickHandler(instance, accounts)
    }
  }, [logoutFlag])

  useEffect(() => {
    if (isReloading) setTimeout(() => {
      setReloading(false)

    }, 1000)
  }, [isReloading])

  const logout = () => {
    signOutClickHandler(instance, accounts)
  }

  const headerSetReloading = (bool: boolean) => {
    if (bool && (userStatus?.status !== 'Prospect' || tokenOnboarding)) {
      navigate(userStatus?.navigateTo, {})
    }
    setReloading(bool)
  }
  //@ts-ignore:next-line
  const isAdmin: boolean = token && jose?.decodeJwt(token).extension_IsAdmin
  const isLoading = isAuthenticated && location?.pathname === '/'
  const NonInfinity: FC = () => {
    // Prevent infinite loading, it will logout after X time if it takes to long
    setTimeout(() => {
      setLogoutFlag(true)
    }, 60000) // 1 minute

    return <Spinner />
  }

  return (
    <Suspense fallback={<Spinner />}>
      <OnboardingContextProvider>
        {!isLoading && (
          <Header
            isAdmin={isAdmin}
            employeeMode={userStatus?.status === 'Employee'}
            signOutClickHandler={logout}
            setReloading={headerSetReloading}
          />
        )}
        {isReloading ? (
          <Spinner />
        ) : (
          <Main $settlements={location.pathname.startsWith('/settlements')}>
            <Routes>
              <Route path='/' element={isAuthenticated ? <NonInfinity /> : <Home />} />
              <Route path='/error/:statusKey' element={<Error logout={logout} />} />

              {isAuthenticated && (
                <Route path='/hold' element={<NoAccountAssociated />} />
              )}

              {isAuthenticated && (
                <Route path='/rejected' element={<UserRejected />} />
              )}

              {isAuthenticated && (
                <Route path='/onboarding/noapplications' element={<NoApplications />} />
              )}

              {isAuthenticated && ['Employee'].includes(userStatus?.status) && (
                <Route path='/onboarding/applications-dashboard' element={<DocDashboard />} />
              )}

              {isAuthenticated && ['Employee', 'Prospect'].includes(userStatus?.status) && (
                <Route path='/onboarding/general-message' element={<GeneralMessage />} />
              )}

              {isAuthenticated && (userStatus?.status === 'Prospect' || userStatus?.status === 'Employee') && (
                <Route path='/onboarding/:flowKey/:step' element={<Onboarding />} />
              )}

              {isAuthenticated && userStatus?.status === 'Employee' && (
                <Route path='/manage-client' element={<ManageClient />} />
              )}

              {isAuthenticated && isAdmin && ['Client', 'Prospect'].includes(userStatus?.status) && (
                <Route path='/manage-team' element={<ManageTeam />} />
              )}

              {isAuthenticated && (['Client', 'Employee'].includes(userStatus?.status)) && userStatus?.status !== 'Prospect' && (
                <Route path='/reports' element={<Reports />} />
              )}

              {isAuthenticated && (['Clearance'].includes(userStatus?.status)) && (
                <Route path='/statements' element={<ClearanceStatements />} />
              )}

              {isAuthenticated && (['Client', 'Employee'].includes(userStatus?.status)) && (
                <Route path='/home' element={<Dashboard />} />
              )}

              {isAuthenticated && (['Client', 'Employee'].includes(userStatus?.status)) && (
                <Route path='/messages' element={<Messages />} />
              )}

              {isAuthenticated && (
                <Route path='/profile' element={<Profile />} />
              )}

              {/*
              {isAuthenticated && (
                <Route path='/settings' element={<AccountSettings />} />
              )}
              */}
              {isAuthenticated && (['Client', 'Employee'].includes(userStatus?.status)) && (
                <Route path='/news' element={<News />} />
              )}

              {isAuthenticated && userStatus?.status === 'Employee' && (
                <Route path='/manage-clearance' element={<ManageClearance />} />
              )}

              {isAuthenticated && (
                <Route path='/dashboard' element={<DashboardWrapper />} />
              )}

              {isAuthenticated && (
                <Route path='/accounts' element={<DashboardWrapper pageToShow='accounts' />} />
              )}

              {isAuthenticated && (['Client', 'Employee'].includes(userStatus?.status)) && (
                <Route path='/:type/:asset' element={<DashboardWrapper />} />
              )}

              {isAuthenticated && 'Client' === userStatus?.status && (
                <Route path='/settlements' element={<Settlements />}>
                  <Route index element={<Navigate to='control-panel' replace />} />
                  <Route path='control-panel' element={<ControlPanel />} />
                  <Route path='positions'>
                    <Route index element={<Navigate to='outstanding' replace />} />
                    <Route path='outstanding' element={<PositionsOutstanding />}>
                      <Route index element={<Navigate to='summary' replace />} />
                      <Route path='summary' element={<SummaryTab />} />
                      <Route path='positions' element={<PositionsTab />} />
                      <Route path='trades' element={<TradesTab />} />
                    </Route>
                    <Route path='agreed' element={<PositionsAgreed />} />
                  </Route>
                  <Route path='cash-ledger' element={<CashLedger />} />
                </Route>
              )}

              {isAuthenticated && 'Employee' === userStatus?.status && (
                <Route path='/settlements' element={<Settlements />}>
                  <Route index element={<Navigate to='control-panel' replace />} />
                  <Route path='control-panel' element={<EmployeeControlPanel />} />
                  <Route path='positions'>
                    <Route index element={<Navigate to='outstanding' replace />} />
                    <Route path='outstanding' element={<EmployeePositionsOutstanding />}>
                      <Route index element={<Navigate to='summary' replace />} />
                      <Route path='summary' element={<EmployeeSummaryTab />} />
                      <Route path='positions' element={<EmployeePositionsTab />} />
                      <Route path='trades' element={<EmployeeTradesTab />} />
                    </Route>
                    <Route path='agreed' element={<EmployeePositionsAgreed />} />
                  </Route>
                  <Route path='cash-ledger' element={<EmployeeCashLedger />} />
                  <Route path='pending-payments' element={<PendingPayments />} />
                </Route>
              )}

              <Route path='*' element={<Home />} />
            </Routes>
          </Main>
        )}
        {!isLoading && (location.pathname.startsWith('/settlements') ? <FooterSettlements /> : <Footer />)}
        <Modal />
      </OnboardingContextProvider>
    </Suspense>
  )
}

export default App
